import { MouseEvent, PointerEvent, useContext, useState } from 'react';
import { IDancer, IDancerPathNode, getDancerPathColor } from '../../models/Timeline';
import useActions from '../../hooks/use-actions';
import useTypedSelector from '../../hooks/use-type-selectors';
import { AudioPlaybackState } from '../../models/AudioPlayback';
import { TimelineContainerContext } from '../../context/TimelineContainerContext';
import { ImagesContext } from '../../context/ImagesContext';

interface ITimelinePathNodeTrackProps {
	dancer: IDancer;
	trackContentContainer: HTMLElement | null;
	trackHeight: number;
	yOffset: number;
}

enum PathNodeDragType {
	Time,
	Delay,
	TimeAndFuture, // The time of the selected node and all nodes to the right of it
}

interface IPathNodeDrag {
	pathNodeId: number;
	posX: number;
	pointerOffsetX: number;
	type: PathNodeDragType;
	originalTime: number;
}

const TimelinePathNodeTrack: React.FC<ITimelinePathNodeTrackProps> = ({
	dancer,
	trackContentContainer,
	trackHeight,
	yOffset,
}: ITimelinePathNodeTrackProps) => {
	const { stopPlayback, setDancerPathNode, selectDancerPathNode, moveFutureDancerPathNodes } =
		useActions();
	const audioPlayback = useTypedSelector((state) => state.audioPlayback);
	const selectedDancerPathNode = useTypedSelector(
		(state) => state.undoable.timeline.selectedDancerPathNode
	);
	const [pathNodeDrag, setPathNodeDrag] = useState<IPathNodeDrag | null>(null);
	const { timelineScale } = useContext(TimelineContainerContext);
	const { images, getPathNodeImage, getTintedImage } = useContext(ImagesContext);

	if (dancer.pathNodes.length === 0) return <></>;
	let pathNodesJsx: JSX.Element[] = [];

	function generatePathNodeJsx(pathNode: IDancerPathNode, iconSrc: string, color: string) {
		const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
			if (trackContentContainer === null) return;

			event.currentTarget.setPointerCapture(event.pointerId);
			event.preventDefault();
			const trackContentContainerRect = trackContentContainer.getBoundingClientRect();

			let dragType: PathNodeDragType = PathNodeDragType.Time;
			if (event.button === 2) {
				dragType = PathNodeDragType.Delay;
			} else if (event.shiftKey) {
				dragType = PathNodeDragType.TimeAndFuture;
			}

			const pointerOffsetX =
				event.clientX -
				trackContentContainerRect.left -
				(dragType === PathNodeDragType.Delay
					? pathNode.time + pathNode.delay
					: pathNode.time) *
					timelineScale;

			setPathNodeDrag({
				pathNodeId: pathNode.id,
				posX: event.clientX - pointerOffsetX - trackContentContainerRect.left,
				pointerOffsetX: pointerOffsetX,
				type: dragType,
				originalTime: pathNode.time,
			});

			if (
				selectedDancerPathNode?.dancerId === dancer.id &&
				selectedDancerPathNode.pathNodeId === pathNode.id
			) {
				selectDancerPathNode(null);
			} else {
				selectDancerPathNode({ dancerId: dancer.id, pathNodeId: pathNode.id });
			}

			if (audioPlayback.state === AudioPlaybackState.PlayingTimeline) {
				stopPlayback();
			}
		};

		const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
			if (pathNodeDrag === null || pathNodeDrag.pathNodeId !== pathNode.id) return;
			if (trackContentContainer === null) return;

			const trackContentContainerRect = trackContentContainer.getBoundingClientRect();
			setPathNodeDrag({
				...pathNodeDrag,
				posX: Math.max(
					0,
					event.clientX - pathNodeDrag.pointerOffsetX - trackContentContainerRect.left
				),
			});
		};

		const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
			if (pathNodeDrag === null || pathNodeDrag.pathNodeId !== pathNode.id) return;
			if (trackContentContainer === null) {
				setPathNodeDrag(null);
				return;
			}

			event.preventDefault();

			const trackContentContainerRect = trackContentContainer.getBoundingClientRect();

			const time =
				Math.max(
					0,
					event.clientX - pathNodeDrag.pointerOffsetX - trackContentContainerRect.left
				) / timelineScale;

			switch (pathNodeDrag.type) {
				case PathNodeDragType.Time: {
					setDancerPathNode(
						dancer.id,
						pathNode.id,
						pathNode.name,
						time,
						pathNode.delay,
						pathNode.pos
					);
					break;
				}
				case PathNodeDragType.Delay: {
					setDancerPathNode(
						dancer.id,
						pathNode.id,
						pathNode.name,
						pathNode.time,
						time - pathNode.time,
						pathNode.pos
					);
					break;
				}
				case PathNodeDragType.TimeAndFuture: {
					moveFutureDancerPathNodes(
						dancer.id,
						pathNode.id,
						time - pathNodeDrag.originalTime
					);
					break;
				}
			}

			setPathNodeDrag(null);
		};

		const onContextMenu = (event: MouseEvent) => {
			event.preventDefault();
			return false;
		};

		let showDelayNode = pathNode.delay > 0;
		if (pathNodeDrag !== null) {
			let dragTime = pathNodeDrag.posX / timelineScale;
			if (pathNodeDrag.pathNodeId === pathNode.id) {
				if (pathNodeDrag.type === PathNodeDragType.Delay) {
					pathNode = { ...pathNode, delay: Math.max(0, dragTime - pathNode.time) };
					showDelayNode = true;
				} else {
					pathNode = { ...pathNode, time: dragTime };
				}
			} else if (
				pathNodeDrag.type === PathNodeDragType.TimeAndFuture &&
				pathNode.time > pathNodeDrag.originalTime
			) {
				pathNode = {
					...pathNode,
					time: pathNode.time + (dragTime - pathNodeDrag.originalTime),
				};
			}
		}

		if (showDelayNode) {
			return [
				<div
					key={pathNode.id + 'DelayedLine'}
					className='timelinePathNodeDelayLine'
					style={{
						left: pathNode.time * timelineScale,
						width: pathNode.delay * timelineScale,
						top: yOffset + 0.5 * trackHeight,
						backgroundColor: color,
					}}
				></div>,
				<img
					className={`timelinePathNode${
						selectedDancerPathNode?.dancerId === dancer.id &&
						selectedDancerPathNode.pathNodeId === pathNode.id
							? ' timelinePathNode--selected'
							: ''
					}`}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onContextMenu={onContextMenu}
					key={pathNode.id + 'Delayed'}
					src={iconSrc}
					draggable={false}
					style={{
						height: trackHeight,
						width: trackHeight,
						left: (pathNode.time + pathNode.delay) * timelineScale,
					}}
				></img>,
				<img
					className={`timelinePathNode${
						selectedDancerPathNode?.dancerId === dancer.id &&
						selectedDancerPathNode.pathNodeId === pathNode.id
							? ' timelinePathNode--selected'
							: ''
					}`}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onContextMenu={onContextMenu}
					key={pathNode.id}
					src={iconSrc}
					draggable={false}
					style={{
						height: trackHeight,
						width: trackHeight,
						left: pathNode.time * timelineScale,
					}}
				></img>,
			];
		} else {
			return [
				<img
					className={`timelinePathNode${
						selectedDancerPathNode?.dancerId === dancer.id &&
						selectedDancerPathNode.pathNodeId === pathNode.id
							? ' timelinePathNode--selected'
							: ''
					}`}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onContextMenu={onContextMenu}
					key={pathNode.id}
					src={iconSrc}
					draggable={false}
					style={{
						height: trackHeight,
						width: trackHeight,
						left: pathNode.time * timelineScale,
					}}
				></img>,
			];
		}
	}

	const pathColor = getDancerPathColor(dancer.pathColor);
	const pathNodes = dancer.pathNodes;

	// First Node
	const firstTintedImage = getTintedImage(images.alphaNode, pathColor);
	pathNodesJsx.push(
		...generatePathNodeJsx(pathNodes[0], firstTintedImage?.dataUrl || '', pathColor)
	);

	// Middle Nodes
	if (pathNodes.length > 2) {
		const tintedImage = getTintedImage(getPathNodeImage(dancer.pathNodeShape), pathColor);

		for (let i = 1; i < pathNodes.length - 1; i++) {
			const pathNode = pathNodes[i];
			pathNodesJsx.push(
				...generatePathNodeJsx(pathNode, tintedImage?.dataUrl || '', pathColor)
			);
		}
	}

	// Last Node
	if (pathNodes.length > 1) {
		const lastTintedImage = getTintedImage(images.omegaNode, pathColor);
		pathNodesJsx.push(
			...generatePathNodeJsx(
				pathNodes[pathNodes.length - 1],
				lastTintedImage?.dataUrl || '',
				pathColor
			)
		);
	}

	return <>{pathNodesJsx}</>;
};

export default TimelinePathNodeTrack;
